import React, { useEffect, useState } from 'react';
import { get } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { fmtBRL, fmtDate } from '../lib/format';
import { Loader2 } from 'lucide-react';

const EXPENSE_TYPES = ['CUSTO', 'DESPESA', 'IMPOSTO', 'INVESTIMENTO'];

/**
 * Drill-down do Resumo Geral do Fluxo de Caixa.
 *  - key = 'entradas' | 'saidas'
 *  - col = 'realizado' (entries) | 'previsao' (entries.amount_forecast + receivables/payables pendentes)
 */
export default function CashflowSummaryDrillDown({ open, onClose, drill, competence, companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !drill) return;
    setLoading(true);
    (async () => {
      // IMPORTANTE: usar include_provisioned=false para não duplicar receivables/payables.
      // O backend de /cashflow/summary soma SEPARADAMENTE:
      //   - entries realizadas (coleção db.entries) com amount/amount_forecast
      //   - receivables/payables pendentes com due_date na competência
      // Então o drill-down replica a mesma lógica aqui para que o total do modal
      // bata exatamente com o Total do Resumo Geral.
      const entryParams = { competence, include_provisioned: false, ...(companyId && companyId !== 'all' ? { company_id: companyId } : {}) };
      const companyParams = companyId && companyId !== 'all' ? { company_id: companyId } : {};
      const [allEntries, allReceivables, allPayables] = await Promise.all([
        get('/entries', entryParams),
        drill.col === 'previsao' && drill.key === 'entradas' ? get('/receivables', companyParams) : Promise.resolve([]),
        drill.col === 'previsao' && drill.key === 'saidas' ? get('/payables', companyParams) : Promise.resolve([]),
      ]);

      const list = [];
      if (drill.key === 'entradas') {
        if (drill.col === 'realizado') {
          allEntries.filter((e) => e.type === 'RECEITA').forEach((e) => {
            list.push({ id: e.id, kind: 'entry', date: e.date, description: e.description, amount: e.amount, status: e.status, source: 'Lançamento Realizado' });
          });
        } else {
          // PREVISÃO entradas = amount_forecast de entries da competência + receivables pendentes da competência
          allEntries.filter((e) => e.type === 'RECEITA').forEach((e) => {
            const f = e.amount_forecast ?? e.amount;
            list.push({ id: e.id + '-f', kind: 'entry-forecast', date: e.date, description: `${e.description} (previsto original)`, amount: f, status: 'PREVISTO', source: 'Previsão de entrada (entry)' });
          });
          allReceivables.filter((r) => (r.due_date || '').startsWith(competence) && r.status !== 'PAGO').forEach((r) => {
            list.push({ id: r.id, kind: 'receivable', date: r.due_date, description: `${r.client || ''} - ${r.description || ''}`, amount: r.amount, status: r.status, source: 'Conta a Receber pendente' });
          });
        }
      } else {
        // saidas
        if (drill.col === 'realizado') {
          allEntries.filter((e) => EXPENSE_TYPES.includes(e.type)).forEach((e) => {
            list.push({ id: e.id, kind: 'entry', date: e.date, description: e.description, amount: e.amount, status: e.status, source: `Lançamento Realizado (${e.type})` });
          });
        } else {
          allEntries.filter((e) => EXPENSE_TYPES.includes(e.type)).forEach((e) => {
            const f = e.amount_forecast ?? e.amount;
            list.push({ id: e.id + '-f', kind: 'entry-forecast', date: e.date, description: `${e.description} (previsto original)`, amount: f, status: 'PREVISTO', source: `Previsão de saída (${e.type})` });
          });
          allPayables.filter((p) => (p.due_date || '').startsWith(competence) && p.status !== 'PAGO').forEach((p) => {
            list.push({ id: p.id, kind: 'payable', date: p.due_date, description: `${p.supplier || ''} - ${p.description || ''}`, amount: p.amount, status: p.status, source: 'Conta a Pagar pendente' });
          });
        }
      }
      list.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setItems(list);
      setLoading(false);
    })();
  }, [open, drill, competence, companyId]);

  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const colorClass = drill?.key === 'entradas' ? 'emerald-text' : 'red-text';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="dialog-cashflow-drilldown">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between">
            <span>{drill?.label || 'Detalhamento'}</span>
            <span className={`font-mono text-base ${colorClass}`}>{fmtBRL(total)}</span>
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">{items.length} linha{items.length === 1 ? '' : 's'} compõem este total na competência {competence}</div>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center"><Loader2 className="animate-spin text-amber-400" size={20} /></div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm">Sem linhas para este filtro.</div>
        ) : (
          <div className="overflow-x-auto border border-zinc-800 rounded-md">
            <table className="w-full ceap-table" data-testid="cashflow-drilldown-table">
              <thead><tr><th>Data</th><th>Descrição</th><th>Origem</th><th className="text-center">Status</th><th className="text-right">Valor</th></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="font-mono text-xs text-zinc-400">{fmtDate(it.date)}</td>
                    <td className="text-zinc-100">{it.description}</td>
                    <td className="text-xs text-zinc-500">{it.source}</td>
                    <td className="text-center">
                      <span className={`pill text-[10px] ${it.status === 'PAGO' || it.status === 'CONCILIADO' ? 'pill-success' : 'pill-warning'}`}>{it.status}</span>
                    </td>
                    <td className={`text-right font-mono ${colorClass}`}>{fmtBRL(it.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-900/40 border-t-2 border-amber-700/40">
                  <td colSpan={4} className="text-right text-zinc-100 font-medium">TOTAL</td>
                  <td className={`text-right font-mono text-base font-bold ${colorClass}`}>{fmtBRL(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
